import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Shield,
  Users,
  UserPlus,
  Trash2,
  Loader2,
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const AdminAdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const navigate = useNavigate();

  const fetchAdmins = useCallback(async () => {
    try {
      const t = localStorage.getItem("adminToken");
      if (!t) return;
      const response = await axios.get(`${API}/admin/admins`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setAdmins(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const adminData = localStorage.getItem("adminData");
    const token = localStorage.getItem("adminToken");
    if (!token || !adminData) {
      navigate("/admin/login");
      return;
    }
    const parsed = JSON.parse(adminData);
    if (parsed.role !== "super_admin") {
      navigate("/admin/dashboard");
      return;
    }
    setAdmin(parsed);
    fetchAdmins();
  }, [navigate, fetchAdmins]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("All fields are required");
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(`${API}/admin/create`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin account created");
      setShowCreateModal(false);
      setForm({ email: "", password: "", name: "" });
      fetchAdmins(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin account deleted");
      fetchAdmins(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">Admin Portal</span>
                <Badge className="ml-2 bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs">
                  {admin?.role}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm hidden sm:block">{admin?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button variant="outline" className="btn-secondary">
              <LayoutDashboard className="w-4 h-4 mr-2" />Dashboard
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button variant="outline" className="btn-secondary">
              <Users className="w-4 h-4 mr-2" />Users
            </Button>
          </Link>
          <Link to="/admin/requests">
            <Button variant="outline" className="btn-secondary">
              <ShoppingBag className="w-4 h-4 mr-2" />Service Requests
            </Button>
          </Link>
          <Link to="/admin/transactions">
            <Button variant="outline" className="btn-secondary">
              <CreditCard className="w-4 h-4 mr-2" />Transactions
            </Button>
          </Link>
          <Link to="/admin/admins">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white">
              <UserPlus className="w-4 h-4 mr-2" />Admins
            </Button>
          </Link>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Accounts</h1>
            <p className="text-slate-400 mt-1">Manage system administrator accounts</p>
          </div>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-admin-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create Admin
          </Button>
        </div>

        <Card className="card-dashboard">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-800">
              {admins.map((a) => (
                <div key={a.admin_id} className="flex items-center justify-between p-4 hover:bg-slate-800/30" data-testid={`admin-row-${a.admin_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{a.name}</p>
                      <p className="text-slate-400 text-sm">{a.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={
                      a.role === "super_admin"
                        ? "bg-violet-500/10 text-violet-400 border-violet-500/30"
                        : "bg-slate-500/10 text-slate-400 border-slate-500/30"
                    }>
                      {a.role === "super_admin" ? "Super Admin" : "Admin"}
                    </Badge>
                    {a.admin_id !== admin?.admin_id && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(a.admin_id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-admin-${a.admin_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Create Admin Account</DialogTitle>
              <DialogDescription className="text-slate-400">
                Add a new administrator to the system
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name</Label>
                <Input
                  placeholder="John Doe"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-default"
                  data-testid="admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  placeholder="admin@company.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input-default"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password</Label>
                <Input
                  type="password"
                  placeholder="Strong password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-default"
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700 text-white" disabled={creating} data-testid="submit-create-admin">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : "Create Admin"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminAdminsPage;
